import type {
  BoardGeometry,
  Cell,
  MoveTarget,
  PlacedToken,
  PlayerColor,
} from '@/shared/board/types'
import type { Combo, Move, MoveStep, TokenPosition } from '@/shared/game/types'

/** Цель хода вместе с тем, что отправлять серверу. */
export type MoveOption = MoveTarget & { steps: MoveStep[] }

/**
 * Раскладка позиций фишек по клеткам доски.
 *
 * Позиция с сервера — это «где», а не «на какой клетке»: место (двор, кольцо,
 * дом) и индекс внутри него. Перевод в координаты делается здесь, по той же
 * геометрии, что рисует доску, — второго источника правды не заводим.
 */
export function cellOf(
  board: BoardGeometry,
  color: PlayerColor,
  position: TokenPosition,
  token: number,
): Cell | null {
  const player = board.players.find((candidate) => candidate.color === color)

  if (player === undefined) return null

  if (position.location === 'ring') return board.ring[position.index] ?? null
  if (position.location === 'home') return player.home[position.index] ?? null

  // Во дворе фишка стоит на своём месте: мест ровно столько, сколько фишек.
  return player.base.slots[token] ?? null
}

/**
 * Все фишки партии в координатах клеток.
 *
 * pickable — только свои и только те, которыми сейчас есть чем сходить:
 * подсветка чужих вариантов правилами запрещена, а своих без хода —
 * бессмысленна.
 */
export function placeTokens(
  board: BoardGeometry,
  tokens: Record<string, TokenPosition[]> | null,
  options: {
    mine: PlayerColor | null
    moves: Move[]
    selected: number | null
    /** Фишка, которая сейчас едет по доске: её видно приподнятой. */
    walking?: { color: PlayerColor; token: number } | null
  },
): PlacedToken[] {
  if (tokens === null) return []

  const movable = new Set(options.moves.map((move) => move.token))
  const placed: PlacedToken[] = []

  for (const [color, positions] of Object.entries(tokens)) {
    positions.forEach((position, token) => {
      const cell = cellOf(board, color as PlayerColor, position, token)

      if (cell === null) return

      const mine = color === options.mine

      placed.push({
        color: color as PlayerColor,
        token,
        cell,
        selected: mine && options.selected === token,
        pickable: mine && movable.has(token),
        moving: options.walking?.color === color && options.walking?.token === token,
        waiting: position.location === 'base',
      })
    })
  }

  return placed
}

/** Ключ хода. Префикс разделяет одиночный ход и ход на сумму кубиков. */
export function moveKey(move: Move): string {
  return `m:${move.token}-${move.die}-${move.kind}`
}

/**
 * Ключ хода на сумму.
 *
 * В него входят виды шагов, а не только кубики. Иначе «шаг на 1, потом
 * прыжок на 3» и «шаг на 1, потом шаг на 3» получают один ключ — а это
 * разные ходы, ведущие в разные клетки. По клику находился первый из них,
 * и фишка уходила не туда, куда игрок целился.
 */
export function comboKey(combo: Combo): string {
  const steps = combo.steps.map((step) => `${step.die}${step.kind[0]}`).join('>')

  return `c:${combo.token}-${steps}-${combo.to.location}${combo.to.index}`
}

/**
 * Куда может пойти выбранная фишка — одиночными ходами и сразу на сумму.
 *
 * Вместе с клеткой возвращается и то, что отправлять серверу: интерфейс не
 * пересобирает ход по клетке, а несёт готовые шаги, которые движок уже
 * признал допустимыми.
 */
export function optionsOf(
  board: BoardGeometry,
  color: PlayerColor,
  moves: Move[],
  combos: Combo[],
  selected: number | null,
): MoveOption[] {
  if (selected === null) return []

  const single = moves
    .filter((move) => move.token === selected)
    .map((move): MoveOption | null => {
      const cell = cellOf(board, color, move.to, move.token)

      if (cell === null) return null

      const what =
        move.kind === 'exit' ? 'выход из двора' : move.kind === 'jump' ? 'прыжок по углам' : 'ход'

      return {
        key: moveKey(move),
        cell,
        victims: move.captures === null ? [] : [move.captures.color],
        jump: move.kind === 'jump',
        hint: `${what} на ${move.die}${move.captures === null ? '' : ' — срубить фишку'}`,
        pips: move.die,
        steps: [{ token: move.token, die: move.die, kind: move.kind }],
        trail: trailOf(board, color, move.token, move.from, move.path),
      }
    })

  const sums = combos
    .filter((combo) => combo.token === selected)
    .map((combo): MoveOption | null => {
      const cell = cellOf(board, color, combo.to, combo.token)

      if (cell === null) return null

      return {
        key: comboKey(combo),
        cell,
        victims: combo.captures.map((one) => one.color),
        jump: combo.steps.some((step) => step.kind === 'jump'),
        hint: `сумма ${combo.dice.join(' + ')}${combo.captures.length === 0 ? '' : ' — срубить фишку'}`,
        pips: combo.dice.reduce((sum, die) => sum + die, 0),
        steps: combo.steps,
        trail: trailOf(
          board,
          color,
          combo.token,
          moves.find((move) => move.token === combo.token)?.from ?? null,
          combo.path,
        ),
      }
    })

  // Ход на сумму может привести туда же, куда одиночный: тогда полезен
  // именно он — иначе второй кубик придётся играть отдельно.
  const options = [...sums, ...single].filter((option): option is MoveOption => option !== null)
  const seen = new Set<string>()

  return options.filter((option) => {
    const cell = `${option.cell.row}:${option.cell.col}`

    if (seen.has(cell)) return false

    seen.add(cell)

    return true
  })
}

/** Путь фишки от её клетки до цели — в клетках доски. */
function trailOf(
  board: BoardGeometry,
  color: PlayerColor,
  token: number,
  from: TokenPosition | null,
  path: TokenPosition[],
): Cell[] {
  return [...(from === null ? [] : [from]), ...path]
    .map((position) => cellOf(board, color, position, token))
    .filter((cell): cell is Cell => cell !== null)
}
